import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query'
import { DateTime } from 'luxon'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { DeleteZoneRequestDTO } from '@core/api/dtos/request/zone/delete'
import { ExportZoneConnectionsRequest } from '@core/api/dtos/request/zone/exportZoneConnections'
import { UpdateZoneRequestDTO } from '@core/api/dtos/request/zone/update'
import { UpdateZonePlaylistsRequestDTO } from '@core/api/dtos/request/zone/updatePlaylists'
import { UpdateZoneSchedulesRequestDTO } from '@core/api/dtos/request/zone/updateSchedules'
import { Playlist } from '@core/api/entities/playlist/playlist'
import { Schedule } from '@core/api/entities/schedule'
import { Event } from '@core/api/entities/zone/event'
import { Occupation } from '@core/api/entities/zone/occupation'
import { Onelink } from '@core/api/entities/zone/onelink'
import { Zone } from '@core/api/entities/zone/zone'
import { ZoneConnection } from '@core/api/entities/zone/zoneConnection'
import { ZoneGeneralConnection } from '@core/api/entities/zone/zoneGeneralConnection'
import { PlaylistMapper } from '@core/api/mappers/playlist/playlistMapper'
import { ScheduleMapper } from '@core/api/mappers/scheduleMapper'
import { EventMapper } from '@core/api/mappers/zone/eventMapper'
import { OccupationMapper } from '@core/api/mappers/zone/occupationMapper'
import { OnelinkMapper } from '@core/api/mappers/zone/onelinkMapper'
import { ZoneConnectionMapper } from '@core/api/mappers/zone/zoneConnectionMapper'
import { ZoneGeneralConnectionMapper } from '@core/api/mappers/zone/zoneGeneralConnectionMapper'
import { ZoneMapper } from '@core/api/mappers/zone/zoneMapper'
import zoneService from '@core/api/services/zoneService'
import { useAccountConfig } from '@core/hooks/useAccountConfig'

import { DEFAULT_PARAMS } from './common'
import { CreateZoneRequestDTO } from '../api/dtos/request/zone/create'
import { RefreshZoneRequestDTO } from '../api/dtos/request/zone/refresh'
import { SendCodeEmailRequestDTO } from '../api/dtos/request/zone/sendCodeEmail'

/**
 * Get zones
 */
export const useZones = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<PaginatedResponse<Zone>, Error> => {
	const accountConfig = useAccountConfig(config, true)

	const QUERY_KEY = [
		'zones',
		{
			...accountConfig?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService.paginateZones(accountConfig).then(({ data: response }) => {
				return {
					data: ZoneMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Get zone
 */
export const useZone = ({
	playerId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Zone, Error> => {
	const QUERY_KEY = [
		'zone',
		playerId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService.getZone(playerId, config).then(({ data: response }) => {
				return ZoneMapper.toEntity(response.data)
			}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Create zone
 */
export const useCreateZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Zone,
	Error,
	CreateZoneRequestDTO,
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<Zone, Error, CreateZoneRequestDTO, any>({
		mutationFn: (data: CreateZoneRequestDTO) =>
			zoneService.createZone(data, config).then(({ data: response }) => {
				return ZoneMapper.toEntity(response.data)
			}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zones'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Update zone
 */
export const useUpdateZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Zone,
	Error,
	{ zoneId: number; data: UpdateZoneRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		Zone,
		Error,
		{ zoneId: number; data: UpdateZoneRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZoneRequestDTO
		}) =>
			zoneService.patchZone(zoneId, data, config).then(({ data: response }) => {
				return ZoneMapper.toEntity(response.data)
			}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zones'] })
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

export type ExportZonesResult =
	| { outcome: 'download'; blob: Blob }
	| { outcome: 'emailQueued' }

/**
 * Export zones.
 * When the backend responds with 200, resolves with a downloadable Blob.
 * When the backend responds with 204 (list too large), resolves with
 * { outcome: 'emailQueued' } to signal that the file will be delivered by email.
 */
export const useExportZones = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<ExportZonesResult, Error> => {
	return useMutation({
		mutationFn: () =>
			zoneService.exportZones(config).then((response) => {
				if (response.status === 204) {
					return { outcome: 'emailQueued' } as ExportZonesResult
				}
				const blob = new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
				return { outcome: 'download', blob } as ExportZonesResult
			}),
		...options,
	})
}

/**
 * Get general connections
 */
export const useGetGeneralConnections = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<ZoneGeneralConnection, Error> => {
	return useQuery({
		queryKey: [
			'zones',
			'general-connections',
			{
				...config?.params,
			},
		],
		queryFn: () =>
			zoneService.getGeneralConnections(config).then(({ data: response }) => {
				return ZoneGeneralConnectionMapper.toEntity(response.data)
			}),
		...options,
	})
}

/**
 * Export general connections
 */
export const useExportGeneralConnections = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<Blob, Error> => {
	return useMutation({
		mutationFn: () =>
			zoneService.exportGeneralConnections(config).then((response) => {
				const blob = new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
				return blob
			}),
		...options,
	})
}

/**
 * Get zone connections
 */
export const useGetZoneConnections = ({
	uuid,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<ZoneConnection[], Error> => {
	return useQuery({
		queryKey: [
			'zones',
			uuid,
			'zone-connections',
			{
				...config?.params,
			},
		],
		queryFn: () =>
			zoneService
				.getZoneConnections(uuid, config)
				.then(({ data: response }) => {
					return ZoneConnectionMapper.toEntities(response.data)
				}),
		...options,
	})
}

/**
 * Export zone connections
 */
export const useExportZoneConnections = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Blob,
	Error,
	ExportZoneConnectionsRequest
> => {
	return useMutation<Blob, Error, ExportZoneConnectionsRequest, any>({
		mutationFn: (data: ExportZoneConnectionsRequest) =>
			zoneService.exportZoneConnections(data, config).then((response) => {
				const blob = new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
				return blob
			}),
		...options,
	})
}

/**
 * Get zone occupation
 */
export const useGetZoneOccupation = ({
	accountId,
	zoneId,
	config = {},
	options = {},
}: any = DEFAULT_PARAMS): UseQueryResult<Occupation[], Error> => {
	const buildQueryParams = () => {
		const now = DateTime.now()
		const yesterday = now.minus({ days: 1 })

		// Convert to UTC
		const nowUTC = now.toUTC()
		const yesterdayUTC = yesterday.toUTC()
		const timezoneOffset = -60

		// Format dates as ISO without milliseconds: YYYY-MM-DDTHH:mm:ssZ
		const fromDate = yesterdayUTC.toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'")
		const toDate = nowUTC.toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'")

		return {
			...config?.params,
			from: fromDate,
			to: toDate,
			timezone_offset: timezoneOffset,
		}
	}

	return useQuery({
		// Keep the key stable (no timestamps) so re-renders don't create new queries
		queryKey: ['zone-occupation', accountId, zoneId],
		queryFn: () => {
			return zoneService
				.getZoneOccupation(accountId, zoneId, { params: buildQueryParams() })
				.then(({ data: response }) => {
					return OccupationMapper.toEntities(response.data)
				})
		},
		staleTime: 5 * 60 * 1000, // 5 minutes — occupation data doesn't need instant refresh
		cacheTime: 10 * 60 * 1000, // 10 minutes
		...options,
	})
}

/**
 * Delete zone
 */
export const useDeleteZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: DeleteZoneRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: DeleteZoneRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: DeleteZoneRequestDTO
		}) =>
			zoneService.deleteZone(zoneId, data, config).then(() => {
				// Return void as we don't need the response data
				return
			}),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zones'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Get zone events
 */
export const useGetZoneEvents = ({
	uuid,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<PaginatedResponse<Event>, Error> => {
	const QUERY_KEY = [
		'zones',
		'events',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService.getZoneEvents(uuid, config).then(({ data: response }) => {
				return {
					data: EventMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		...options,
	})
}

/**
 * Validate name availability
 */
export const useValidateNameAvailability = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	boolean,
	Error,
	{ accountId: number; name: string },
	any
> => {
	return useMutation<boolean, Error, { accountId: number; name: string }, any>({
		mutationFn: ({ accountId, name }: { accountId: number; name: string }) =>
			zoneService
				.validateName(accountId, name, config)
				.then(({ data: response }) => response.data?.available ?? false),
		...options,
	})
}

/**
 * Refresh zone
 */
export const useRefreshZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneUUID: string; data: RefreshZoneRequestDTO },
	any
> => {
	return useMutation<
		void,
		Error,
		{ zoneUUID: string; data: RefreshZoneRequestDTO },
		any
	>({
		mutationFn: ({
			zoneUUID,
			data,
		}: {
			zoneUUID: string
			data: RefreshZoneRequestDTO
		}) =>
			zoneService.refresh(zoneUUID, data, config).then(() => {
				// Return void as we don't need the response data
				return
			}),
		...options,
	})
}

export const useSendCodeEmail = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneID: number; data: SendCodeEmailRequestDTO },
	any
> => {
	return useMutation<
		void,
		Error,
		{ zoneID: number; data: SendCodeEmailRequestDTO },
		any
	>({
		mutationFn: ({
			zoneID,
			data,
		}: {
			zoneID: number
			data: SendCodeEmailRequestDTO
		}) =>
			zoneService.sendCodeEmail(zoneID, data, config).then(() => {
				return
			}),
		...options,
	})
}

/**
 * Get zone schedules
 */
export const useGetZoneSchedules = ({
	zoneId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Schedule[], Error> => {
	const QUERY_KEY = ['zone-schedules', zoneId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService
				.getZoneSchedules(zoneId, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Update zone schedules
 */
export const useUpdateZoneSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZoneSchedulesRequestDTO
		}) => zoneService.updateZoneSchedules(zoneId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Get the onelink URL for a zone
 */
export const useOnelink = ({
	zoneId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Onelink, Error> => {
	const QUERY_KEY = ['onelink', zoneId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService
				.getOnelink(zoneId, config)
				.then(({ data: response }) => OnelinkMapper.toEntity(response.data)),
		...options,
	})
}

/**
 * Add schedules to a zone
 */
export const useAddSchedulesToZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZoneSchedulesRequestDTO
		}) => zoneService.addSchedulesToZone(zoneId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Remove schedules from a zone
 */
export const useRemoveSchedulesFromZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: UpdateZoneSchedulesRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZoneSchedulesRequestDTO
		}) => zoneService.removeSchedulesFromZone(zoneId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Get zone playlists (directly assigned)
 */
export const useGetZonePlaylists = ({
	zoneId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Playlist[], Error> => {
	const QUERY_KEY = ['zone-playlists', zoneId, { ...config?.params }]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			zoneService
				.getZonePlaylists(zoneId, config)
				.then(({ data: response }) => {
					return PlaylistMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Add playlists to a zone
 */
export const useAddPlaylistsToZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: UpdateZonePlaylistsRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: UpdateZonePlaylistsRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZonePlaylistsRequestDTO
		}) => zoneService.addPlaylistsToZone(zoneId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Add playlists and/or schedules to a zone
 */
export const useAddContentToZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; playlistIds: number[]; scheduleIds: number[] },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; playlistIds: number[]; scheduleIds: number[] },
		any
	>({
		mutationFn: ({
			zoneId,
			playlistIds,
			scheduleIds,
		}: {
			zoneId: number
			playlistIds: number[]
			scheduleIds: number[]
		}) => {
			const requests: Promise<unknown>[] = []

			if (playlistIds.length > 0) {
				const playlistsPayload: UpdateZonePlaylistsRequestDTO = {
					playlists: playlistIds,
				}
				requests.push(
					zoneService.addPlaylistsToZone(zoneId, playlistsPayload, config)
				)
			}

			if (scheduleIds.length > 0) {
				const schedulesPayload: UpdateZoneSchedulesRequestDTO = {
					schedules: scheduleIds,
				}
				requests.push(
					zoneService.addSchedulesToZone(zoneId, schedulesPayload, config)
				)
			}

			return Promise.all(requests).then(() => {})
		},
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			queryClient.invalidateQueries({ queryKey: ['zone-schedules'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}

/**
 * Remove playlists from a zone
 */
export const useRemovePlaylistsFromZone = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ zoneId: number; data: UpdateZonePlaylistsRequestDTO },
	any
> => {
	const queryClient = useQueryClient()
	const { onSuccess: userOnSuccess, ...restOptions } = options || {}

	return useMutation<
		void,
		Error,
		{ zoneId: number; data: UpdateZonePlaylistsRequestDTO },
		any
	>({
		mutationFn: ({
			zoneId,
			data,
		}: {
			zoneId: number
			data: UpdateZonePlaylistsRequestDTO
		}) => zoneService.removePlaylistsFromZone(zoneId, data, config),
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({ queryKey: ['zone'] })
			queryClient.invalidateQueries({ queryKey: ['zone-playlists'] })
			userOnSuccess?.(data, variables, context)
		},
		...restOptions,
	})
}
